import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Plus, Search, UserCheck, UserX, Mail, Phone, Calendar, User, Loader2, School } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'student' | 'professor' | 'admin';
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  phone?: string;
  bio?: string;
  enrolledCourses?: Array<{ course: { _id: string; title: string; }; enrolledAt?: string; progress?: number; completedModules?: number; }>;
  completedCourses?: number;
  totalSpent?: number;
  stats?: {
    totalStudyTime: number;
    coursesCompleted: number;
    quizzesTaken: number;
    averageScore: number;
  };
  createdCourses?: Array<{ _id: string; title: string; }>;
  class?: { _id: string; name: string; level: string } | null;
  totalStudents?: number;
  totalEarnings?: number;
}

interface CreateUserForm {
  name: string;
  email: string;
  password?: string;
  role: 'student' | 'professor' | 'admin';
  phone?: string;
  isActive: boolean;
}

const AdminUsers: React.FC = () => {
  console.log('[AdminUsers] Rendering component');
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsersCount, setTotalUsersCount] = useState(0);

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUserForDetail, setSelectedUserForDetail] = useState<User | null>(null);
  const [showEditFormModal, setShowEditFormModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateUserForm>();

  // Fetch users with caching and retry logic
  const fetchUsers = useCallback(async () => {
    console.log('[AdminUsers] Fetching users with params:', {
      searchTerm,
      filterRole,
      filterStatus,
      currentPage
    });

    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (searchTerm) params.append('search', searchTerm);
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterStatus !== 'all') params.append('isActive', filterStatus === 'active' ? 'true' : 'false');
      params.append('page', currentPage.toString());
      params.append('limit', '10');

      const response = await api.get(`/users?${params.toString()}`);
      
      if (!response.data?.users || !response.data?.pagination) {
        throw new Error('Invalid response format');
      }

      setUsers(response.data.users);
      setTotalPages(response.data.pagination.pages);
      setTotalUsersCount(response.data.pagination.total);
      
      console.log('[AdminUsers] Users fetched successfully', {
        count: response.data.users.length,
        total: response.data.pagination.total
      });
    } catch (error: any) {
      console.error('[AdminUsers] Fetch error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du chargement des utilisateurs');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterRole, filterStatus, currentPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Helper functions
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-brand-100 text-brand-800';
      case 'professor': return 'bg-brand-100 text-brand-800';
      case 'student': return 'bg-brand-100 text-brand-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrateur';
      case 'professor': return 'Professeur';
      case 'student': return 'Étudiant';
      default: return 'Inconnu';
    }
  };

  // CRUD Operations
  const handleCreateUser = async (data: CreateUserForm) => {
    console.log('[AdminUsers] Creating user:', data);
    try {
      const response = await api.post('/users', data);
      toast.success(response.data.message);
      setShowCreateModal(false);
      fetchUsers();
      reset();
      console.log('[AdminUsers] User created successfully');
    } catch (error: any) {
      console.error('[AdminUsers] Create user error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la création de l\'utilisateur');
    }
  };

  const handleEditUser = async (data: CreateUserForm) => {
    if (!selectedUserForEdit) return;
    console.log('[AdminUsers] Editing user:', selectedUserForEdit._id, data);
    
    try {
      const response = await api.put(`/users/${selectedUserForEdit._id}`, data);
      toast.success(response.data.message);
      setShowEditFormModal(false);
      fetchUsers();
      console.log('[AdminUsers] User updated successfully');
    } catch (error: any) {
      console.error('[AdminUsers] Edit user error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la modification de l\'utilisateur');
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    console.log('[AdminUsers] Toggling status for user:', userId, currentStatus);
    try {
      const response = await api.post(`/users/${userId}/toggle-active`);
      toast.success(response.data.message);
      fetchUsers();
      console.log('[AdminUsers] User status toggled successfully');
    } catch (error: any) {
      console.error('[AdminUsers] Toggle status error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du changement de statut');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    console.log('[AdminUsers] Deleting user:', userId);
    
    try {
      const response = await api.delete(`/users/${userId}`);
      toast.success(response.data.message);
      fetchUsers();
      console.log('[AdminUsers] User deleted successfully');
    } catch (error: any) {
      console.error('[AdminUsers] Delete user error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const handleViewUser = (user: User) => {
    console.log('[AdminUsers] Viewing user details:', user._id);
    setSelectedUserForDetail(user);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedUserForDetail(null);
  };

  // Prevent self-deletion or role change
  const isCurrentUser = (userId: string) => {
    return currentUser?.id === userId;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvel utilisateur
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">Tous les rôles</option>
              <option value="student">Étudiants</option>
              <option value="professor">Professeurs</option>
              <option value="admin">Administrateurs</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="animate-spin h-8 w-8 mx-auto text-gray-400" />
              <p className="mt-2">Chargement des utilisateurs...</p>
            </div>
          ) : (
            <>
              <div className="p-4 text-sm text-gray-600">
                Total utilisateurs: {totalUsersCount}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utilisateur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dernière connexion
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statistiques
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.length > 0 ? (
                      users.map((user) => (
                        <tr key={user._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-brand-600 rounded-full flex items-center justify-center text-white font-medium">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                              {getRoleLabel(user.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`flex items-center text-sm ${user.isActive ? 'text-brand-600' : 'text-brand-600'}`}>
                              {user.isActive ? <UserCheck className="h-4 w-4 mr-1" /> : <UserX className="h-4 w-4 mr-1" />}
                              {user.isActive ? 'Actif' : 'Inactif'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('fr-FR') : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.role === 'student' ? (
                              <div>
                                <div>{user.enrolledCourses?.length || 0} cours inscrit{(user.enrolledCourses?.length || 0) > 1 ? 's' : ''}</div>
                                <div className="text-xs text-gray-500">{user.totalSpent || 0}€ dépensés</div>
                                {user.class && (
                                  <div className="text-xs text-gray-500">
                                    <School className="inline h-3 w-3 mr-0.5" />
                                    {user.class.name}
                                  </div>
                                )}
                              </div>
                            ) : user.role === 'professor' ? (
                              <div>
                                <div>{user.createdCourses?.length || 0} cours créés</div>
                                <div className="text-xs text-gray-500">{user.totalStudents || 0} étudiants</div>
                              </div>
                            ) : (
                              <div className="text-gray-500">-</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => handleViewUser(user)}
                              className="text-brand-600 hover:text-brand-900"
                            >
                              Voir
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user._id, user.isActive)}
                              className={`${user.isActive ? 'text-brand-600 hover:text-brand-900' : 'text-brand-600 hover:text-brand-900'}`}
                              disabled={isCurrentUser(user._id)}
                            >
                              {user.isActive ? 'Désactiver' : 'Activer'}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user._id)}
                              className="text-brand-600 hover:text-brand-900"
                              disabled={isCurrentUser(user._id)}
                            >
                              Supprimer
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-12">
                          <div className="text-gray-400 mb-4">
                            <User className="h-16 w-16 mx-auto" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun utilisateur trouvé</h3>
                          <p className="text-gray-500">
                            {searchTerm || filterRole !== 'all' || filterStatus !== 'all'
                              ? 'Aucun utilisateur ne correspond aux critères de recherche'
                              : 'Aucun utilisateur enregistré'
                            }
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center py-4">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`mx-1 px-3 py-1 rounded-lg ${currentPage === page ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* User Detail Modal */}
        {showDetailModal && selectedUserForDetail && (
          <UserDetailModal 
            user={selectedUserForDetail}
            onClose={closeDetailModal}
            onEdit={() => {
              setSelectedUserForEdit(selectedUserForDetail);
              setShowEditFormModal(true);
              closeDetailModal();
              reset(selectedUserForDetail);
            }}
            getRoleColor={getRoleColor}
            getRoleLabel={getRoleLabel}
          />
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <UserFormModal
            title="Créer un nouvel utilisateur"
            onSubmit={handleSubmit(handleCreateUser)}
            onClose={() => setShowCreateModal(false)}
            register={register}
            errors={errors}
            isSubmitting={loading}
          />
        )}

        {/* Edit User Modal */}
        {showEditFormModal && selectedUserForEdit && (
          <UserFormModal
            title="Modifier l'utilisateur"
            onSubmit={handleSubmit(handleEditUser)}
            onClose={() => setShowEditFormModal(false)}
            register={register}
            errors={errors}
            isSubmitting={loading}
            isEditMode={true}
          />
        )}
      </div>
    </Layout>
  );
};

// Composant modal pour les détails de l'utilisateur
const UserDetailModal = ({ user, onClose, onEdit, getRoleColor, getRoleLabel }: {
  user: User;
  onClose: () => void;
  onEdit: () => void;
  getRoleColor: (role: string) => string;
  getRoleLabel: (role: string) => string;
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {user.name}
              </h2>
              <div className="flex items-center space-x-4">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                  {getRoleLabel(user.role)}
                </span>
                <span className={`flex items-center text-sm ${user.isActive ? 'text-brand-600' : 'text-brand-600'}`}>
                  {user.isActive ? <UserCheck className="h-4 w-4 mr-1" /> : <UserX className="h-4 w-4 mr-1" />}
                  {user.isActive ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center text-gray-600">
                  <Mail className="h-4 w-4 mr-2" />
                  {user.email}
                </div>
                <div className="flex items-center text-gray-600">
                  <Phone className="h-4 w-4 mr-2" />
                  {user.phone || 'N/A'}
                </div>
                <div className="flex items-center text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  Inscrit le {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                </div>
                <div className="flex items-center text-gray-600">
                  <UserCheck className="h-4 w-4 mr-2" />
                  Dernière connexion: {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('fr-FR') : 'N/A'}
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques</h3>
              {user.role === 'student' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">{user.enrolledCourses?.length || 0}</div>
                    <div className="text-sm text-brand-800">Cours inscrits</div>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">{user.stats?.coursesCompleted || 0}</div>
                    <div className="text-sm text-brand-800">Cours terminés</div>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">{(user.totalSpent || 0)?.toLocaleString()}€</div>
                    <div className="text-sm text-brand-800">Total dépensé</div>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">
                      {user.enrolledCourses && user.enrolledCourses.length > 0 ? Math.round((user.stats?.coursesCompleted || 0) / user.enrolledCourses.length * 100) : 0}%
                    </div>
                    <div className="text-sm text-brand-800">Taux de complétion</div>
                  </div>
                </div>
              ) : user.role === 'professor' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">{user.createdCourses?.length || 0}</div>
                    <div className="text-sm text-brand-800">Cours créés</div>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">{user.totalStudents || 0}</div>
                    <div className="text-sm text-brand-800">Total étudiants</div>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">{(user.totalEarnings || 0)?.toLocaleString()}€</div>
                    <div className="text-sm text-brand-800">Revenus générés</div>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">
                      {user.createdCourses && user.createdCourses.length > 0 ? Math.round((user.totalStudents || 0) / user.createdCourses.length) : 0}
                    </div>
                    <div className="text-sm text-brand-800">Moy. étudiants/cours</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Statistiques non applicables pour les administrateurs
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Fermer
          </button>
          <button
            onClick={onEdit}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant modal pour le formulaire utilisateur
const UserFormModal = ({ 
  title, 
  onSubmit, 
  onClose, 
  register, 
  errors, 
  isSubmitting,
  isEditMode = false 
}: {
  title: string;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onClose: () => void;
  register: any;
  errors: any;
  isSubmitting: boolean;
  isEditMode?: boolean;
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={onSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'Le nom est requis' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Jean Dupont"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-brand-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  {...register('email', {
                    required: 'L\'email est requis',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Adresse email invalide'
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="jean.dupont@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-brand-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe {isEditMode && '(laisser vide pour ne pas changer)'}
                </label>
                <input
                  type="password"
                  {...register('password', {
                    required: isEditMode ? false : 'Le mot de passe est requis',
                    minLength: {
                      value: 6,
                      message: 'Le mot de passe doit contenir au moins 6 caractères'
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="••••••••"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-brand-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rôle
              </label>
              <select
                {...register('role', { required: 'Le rôle est requis' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="student">Étudiant</option>
                <option value="professor">Professeur</option>
                <option value="admin">Administrateur</option>
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-brand-600">{errors.role.message}</p>
              )}
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                  defaultChecked
                />
                <span className="ml-2 text-sm text-gray-600">
                  Compte actif (l'utilisateur peut se connecter)
                </span>
              </label>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin h-5 w-5 mx-auto" />
                ) : isEditMode ? (
                  'Mettre à jour'
                ) : (
                  'Créer'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;